import bezier from 'bezier-easing'

import Animation, { type AnimationParams } from './Animation'

declare global {
  interface CanvasRenderingContext2D {
    letterSpacing: string
  }
}

export default class Base {
  declare ['constructor']: typeof Base

  static helpers = {
    isChrome: () => {
      return window.navigator.userAgent.indexOf('Chrome') > -1
    },

    isSafari: () => {
      const isChrome = this.helpers.isChrome()

      return !isChrome && window.navigator.userAgent.indexOf('Safari') > -1
    },

    getRandomIntInclusive: (...args: [number] | [number, number]) => {
      const min = args[1] ? args[0] : 0
      const max = args[1] ? args[1] : args[0]
      const minCeiled = Math.ceil(min)
      const maxFloored = Math.floor(max)

      return Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled)
    },

    getRandomFloatInclusive: (...args: [number] | [number, number]) => {
      const min = args[1] ? args[0] : 0
      const max = args[1] ? args[1] : args[0]

      return Math.random() * (max - min) + min
    },

    delay: (time: number) => new Promise((res) => setTimeout(res, time)),

    toRad: (degrees: number) => (degrees * Math.PI) / 180,

    isTouchDevice: () => {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0
    },

    isPointInCircle: (x: number, y: number, x0: number, y0: number, r: number) => {
      const distPoints = (x - x0) * (x - x0) + (y - y0) * (y - y0)

      return distPoints < r * r
    },

    getBezierEasing: (x1: number, y1: number, x2: number, y2: number) => {
      return bezier(x1, y1, x2, y2)
    },

    createAnimation: (options: AnimationParams) => {
      return Animation.getInstance(options)
    },
  }

  ctx: CanvasRenderingContext2D = null
  node: HTMLCanvasElement = null

  canvasParams = {
    dpr: window.devicePixelRatio,
    w: 0,
    h: 0,
  }

  constructor(node?: HTMLCanvasElement, options?: { width: number; height: number }) {
    if (node) {
      this.setCanvasParams(node, options)
    }
  }

  protected setCanvasParams(node: HTMLCanvasElement, options?: { width: number; height: number }) {
    const width = options?.width || node?.width || 0
    const height = options?.height || node?.height || 0

    const dpr = this.canvasParams.dpr

    this.canvasParams.w = width * dpr
    this.canvasParams.h = height * dpr

    if (!node) {
      return
    }

    this.node = node
    this.node.setAttribute('width', `${this.canvasParams.w}`)
    this.node.setAttribute('height', `${this.canvasParams.h}`)

    this.ctx = node.getContext('2d')
  }

  protected setCleanUpObserver(cb: () => void, timer = 1000) {
    const nodeId = this.node.id || `canvas-${Math.random()}`

    if (!this.node.id) {
      this.node.setAttribute('id', nodeId)
    }

    // clean up if node is dismounted
    const intervalId = setInterval(() => {
      // @ts-ignore
      if (!window[nodeId]) {
        cb()
        clearInterval(intervalId)
      }
    }, timer)
  }
}
